import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../../lib/api';

/**
 * Roles, the permission matrix and user overrides (docs/dev/api-users.md „Roles and permissions", Admin 01/02).
 * Queries under the root `['users']` (a permission change emits `user.updated`).
 */

export interface ActionState {
  allowed: boolean;
  reason: string | null;
}

export interface ValueOption {
  value: string;
  label: string;
}

export interface MatrixRole {
  id: string;
  key: string;
  name: string;
  isSystem: boolean;
}

export interface MatrixRow {
  key: string;
  label: string;
  sensitive: boolean;
  options: ValueOption[];
  cells: { roleId: string; value: string; label: string }[];
}

export interface PermissionMatrix {
  roles: MatrixRole[];
  categories: { label: string; rows: MatrixRow[] }[];
  actions: { save: ActionState; createRole: ActionState };
}

export interface UserPermissionRow {
  key: string;
  label: string;
  sensitive: boolean;
  options: ValueOption[];
  template: ValueOption;
  effective: ValueOption;
  deviates: boolean;
  pending: ValueOption | null;
}

export interface UserPermissions {
  user: { id: string; name: string; shortName: string; roleId: string; roleKey: string; roleName: string };
  title: string;
  subtitle: string;
  banner: { title: string; text: string } | null;
  categories: { label: string; rows: UserPermissionRow[] }[];
  actions: { save: ActionState; reset: ActionState };
}

export const permissionKeys = {
  matrix: () => ['users', 'permissions', 'matrix'] as const,
  user: (id: string) => ['users', 'permissions', 'user', id] as const,
};

export function usePermissionMatrix() {
  return useQuery({
    queryKey: permissionKeys.matrix(),
    queryFn: () => api<PermissionMatrix>('/permissions/matrix'),
    retry: false,
  });
}

export function useUserPermissions(id: string) {
  return useQuery({
    queryKey: permissionKeys.user(id),
    queryFn: () => api<UserPermissions>(`/users/${id}/permissions`),
    retry: false,
  });
}

/** `PUT /roles/:id/permissions` for every role with changes; the last answer is the fresh matrix. */
export function useSaveTemplates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (changes: { roleId: string; changes: { key: string; value: string }[] }[]) => {
      let matrix: PermissionMatrix | null = null;
      for (const entry of changes) {
        matrix = await api<PermissionMatrix>(`/roles/${entry.roleId}/permissions`, {
          method: 'PUT',
          body: { changes: entry.changes },
        });
      }
      return matrix;
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['users', 'permissions'] }),
    onSuccess: (matrix) => {
      if (matrix) queryClient.setQueryData(permissionKeys.matrix(), matrix);
    },
  });
}

export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; basedOnRoleId?: string | undefined }) =>
      api<MatrixRole>('/roles', { method: 'POST', body }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useSaveUserPermissions(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (overrides: { key: string; value: string | null }[]) =>
      api<UserPermissions>(`/users/${id}/permissions`, { method: 'PUT', body: { overrides } }),
    onSuccess: (data) => queryClient.setQueryData(permissionKeys.user(id), data),
  });
}

export function useResetUserPermissions(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<UserPermissions>(`/users/${id}/permissions/reset`, { method: 'POST' }),
    onSuccess: (data) => queryClient.setQueryData(permissionKeys.user(id), data),
  });
}
